import React, { useState, useEffect } from 'react';
import { ref, get, set, remove, push } from 'firebase/database';
import { database } from '../../firebase';
import { parseTextWithImages } from '../../components/parseTextWithImages';

interface Question {
  id: string;
  text: string;
  options: string[];
  answer: string;
  solution?: string;
  subject: string;
  topic: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  exam: string;
  type: 'multiple-choice' | 'integer';
}

export const QuestionBank: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [filters, setFilters] = useState({
    exam: '',
    subject: '',
    difficulty: ''
  });

  const [newQuestion, setNewQuestion] = useState<Partial<Question>>({
    text: '',
    options: ['', '', '', ''],
    answer: '',
    solution: '',
    subject: '',
    topic: '',
    difficulty: 'Medium',
    exam: '',
    type: 'multiple-choice'
  });

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const questionsRef = ref(database, 'questions');
      const snapshot = await get(questionsRef);
      
      if (snapshot.exists()) {
        const questionsData = snapshot.val();
        const questionsList = Object.entries(questionsData).map(([id, data]: [string, any]) => ({
          id,
          ...data
        }));
        setQuestions(questionsList);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveQuestion = async () => {
    try {
      if (editingQuestion) {
        const questionRef = ref(database, `questions/${editingQuestion.id}`);
        await set(questionRef, newQuestion);
        setQuestions(questions.map(q => 
          q.id === editingQuestion.id ? { ...newQuestion, id: editingQuestion.id } as Question : q
        ));
      } else {
        const questionsRef = ref(database, 'questions');
        const newQuestionRef = push(questionsRef);
        await set(newQuestionRef, newQuestion);
        setQuestions([...questions, { ...newQuestion, id: newQuestionRef.key } as Question]);
      }
      
      resetForm();
    } catch (error) {
      console.error('Error saving question:', error);
    }
  };

  const deleteQuestion = async (id: string) => {
    if (!confirm('Are you sure you want to delete this question?')) return;
    
    try {
      const questionRef = ref(database, `questions/${id}`);
      await remove(questionRef);
      setQuestions(questions.filter(q => q.id !== id));
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  const resetForm = () => {
    setNewQuestion({
      text: '',
      options: ['', '', '', ''],
      answer: '',
      solution: '',
      subject: '',
      topic: '',
      difficulty: 'Medium',
      exam: '',
      type: 'multiple-choice'
    });
    setShowAddForm(false);
    setEditingQuestion(null);
  };

  const startEdit = (question: Question) => {
    setNewQuestion(question);
    setEditingQuestion(question);
    setShowAddForm(true);
  };

  const filteredQuestions = questions.filter(q => {
    return (!filters.exam || q.exam === filters.exam) &&
           (!filters.subject || q.subject === filters.subject) &&
           (!filters.difficulty || q.difficulty === filters.difficulty);
  });

  const QuestionForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold">
              {editingQuestion ? 'Edit Question' : 'Add New Question'}
            </h3>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Exam</label>
                <select
                  value={newQuestion.exam}
                  onChange={(e) => setNewQuestion({...newQuestion, exam: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Exam</option>
                  <option value="NDA">NDA</option>
                  <option value="JEE Main">JEE Main</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={newQuestion.subject}
                  onChange={(e) => setNewQuestion({...newQuestion, subject: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Mathematics, Physics"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
                <input
                  type="text"
                  value={newQuestion.topic}
                  onChange={(e) => setNewQuestion({...newQuestion, topic: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Algebra, Mechanics"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                <select
                  value={newQuestion.difficulty}
                  onChange={(e) => setNewQuestion({...newQuestion, difficulty: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Question Text</label>
              <textarea
                value={newQuestion.text}
                onChange={(e) => setNewQuestion({...newQuestion, text: e.target.value})}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Enter question text (supports LaTeX: $formula$ and images: ::::url::::)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
              {newQuestion.options?.map((option, index) => (
                <input
                  key={index}
                  type="text"
                  value={option}
                  onChange={(e) => {
                    const newOptions = [...(newQuestion.options || [])];
                    newOptions[index] = e.target.value;
                    setNewQuestion({...newQuestion, options: newOptions});
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 mb-2"
                  placeholder={`Option ${String.fromCharCode(65 + index)}`}
                />
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Correct Answer</label>
              <input
                type="text"
                value={newQuestion.answer}
                onChange={(e) => setNewQuestion({...newQuestion, answer: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Enter the correct answer"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Solution (Optional)</label>
              <textarea
                value={newQuestion.solution}
                onChange={(e) => setNewQuestion({...newQuestion, solution: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Enter detailed solution"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={saveQuestion}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                {editingQuestion ? 'Update' : 'Add'} Question
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Question Bank ({questions.length})</h3>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Add Question
        </button>
      </div>

      <div className="mb-6 grid grid-cols-3 gap-4">
        <select
          value={filters.exam}
          onChange={(e) => setFilters({...filters, exam: e.target.value})}
          className="px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="">All Exams</option>
          <option value="NDA">NDA</option>
          <option value="JEE Main">JEE Main</option>
        </select>
        <input
          type="text"
          placeholder="Filter by subject"
          value={filters.subject}
          onChange={(e) => setFilters({...filters, subject: e.target.value})}
          className="px-3 py-2 border border-gray-300 rounded-md"
        />
        <select
          value={filters.difficulty}
          onChange={(e) => setFilters({...filters, difficulty: e.target.value})}
          className="px-3 py-2 border border-gray-300 rounded-md"
        >
          <option value="">All Difficulties</option>
          <option value="Easy">Easy</option>
          <option value="Medium">Medium</option>
          <option value="Hard">Hard</option>
        </select>
      </div>

      <div className="space-y-4">
        {filteredQuestions.map((question) => (
          <div key={question.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">{question.exam}</span>
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">{question.subject}</span>
                  <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">{question.difficulty}</span>
                </div>
                <div className="text-sm text-gray-900 mb-2">
                  {parseTextWithImages(question.text)}
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => startEdit(question)}
                  className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteQuestion(question.id)}
                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Delete
                </button>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              Topic: {question.topic} | Answer: {question.answer}
            </div>
          </div>
        ))}
      </div>

      {showAddForm && <QuestionForm />}
    </div>
  );
};